import { getDefinition } from "./catalog";
import { routeExample, viaX } from "./example-layout";
import { SCHEMA_VERSION } from "./schema/version";
import type { Cable, ComponentNode, System, TerminalSide } from "./types";

const pin = (side: TerminalSide, offset: number) => ({ side, offset, pinned: true });
const part = (id: string, definitionId: string, x: number, y: number, extra: Partial<ComponentNode> = {}): ComponentNode =>
  ({ id, kind: "component", definitionId, position: { x, y }, ...extra });
const wire = (id: string, from: [string, string], to: [string, string], lengthM: number, extra: Partial<Cable> = {}): Cable =>
  ({ id, fromNodeId: from[0], fromTerminalId: from[1], toNodeId: to[0], toTerminalId: to[1], lengthM, vdropClass: "critical", ...extra });

/** A scoped house-bank retrofit, informed by the Orion XS and BB10012 manuals.
 * The retained engine/alternator system is outside this DC charging drawing. */
export function buildBoatLifepo4RetrofitSystem(): System {
  const orion = getDefinition("dcdc-victron-orion-xs-50")!;
  const nodes = [
    part("starter", "batt-optima-yellow-d34", 0, 0, {
      label: "Retained AGM start battery", batteryFuse: { ratingA: 60, type: "MRBF" },
      terminalOverrides: { pos: pin("right", .3), neg: pin("top", .3) },
      notes: "The photographed Optima D34 represents a retained AGM starting supply. Engine, alternator, starter cables and their isolation are outside this subsystem. Check the existing battery against the engine requirements. This 60 A fuse protects only the added DC-DC charging branch, not a starter-motor cable.",
    }),
    part("charger", "custom", 410, 0, {
      // The catalog preserves legacy IN−/OUT− handles. This drawing instead
      // shows the physical IN / OUT / GND terminals from Orion XS manual §3.3.
      // The existing shared-negative port model uses GND for both DC sides.
      customDefinition: {
        ...orion,
        terminals: orion.terminals.filter(terminal => terminal.id !== "in-neg")
          .map(terminal => terminal.id === "out-neg" ? { ...terminal, label: "GND" } : { ...terminal }),
      },
      terminalOverrides: { "in-pos": pin("left", .3), "out-pos": pin("bottom", .3), "out-neg": pin("right", .7) },
      notes: "The Orion XS is shown with its three physical IN, OUT and GND connections. One 16 mm² GND wire connects it to the common battery-negative bus. The drawing sizes for its full 50 A limits. Set input current to the alternator's measured spare hot-idle capacity and output current no higher than the battery's 50 A charge limit. Configure engine detection and the battery's lithium profile before use; do not retain a parallel ACR/VSR/Both connection to the lithium bank.",
    }),
    // The manufacturer URL named Rev032 currently serves Rev038 (2026-08-12).
    // Its pp16/22 require 0°C for charging; stale 25°F table/header values
    // conflict, so this example deliberately uses the conservative 0°C limit.
    part("house", "batt-battleborn-100", 410, 250, {
      label: "LiFePO4 house battery", batteryFuse: { ratingA: 60, type: "MRBF" },
      terminalOverrides: { pos: pin("left", .3), neg: pin("right", .75) },
      notes: "Battle Born BB10012, 100 Ah, with integrated BMS. Its manual specifies 50 A maximum charging and 100 A continuous discharge. The BMS does not replace external cable protection. Use the battery's approved lithium profile, disable equalization and temperature compensation, and prevent charging below 0°C. The 60 A MRBF is a Crimp-selected cable-protection adaptation, installed in a compatible terminal fuse holder. Prospective battery fault current and suitability of the exact fuse's interrupt rating remain to be verified.",
    }),
    part("positive", "junction", 300, 276, { label: "Fused house supply" }),
    part("main", "dist-bs-mseries-6005", 292, 390, { rotation: 90, label: "House load switch" }),
    part("panel", "dist-bs-5025", 260, 490, {
      label: "House fuse block",
      terminalOverrides: { "in-pos": pin("top", .5), "in-neg": pin("right", .8), c1: pin("left", .1), n1: pin("left", .4), c4: pin("left", .65), n4: pin("left", .95) },
      notes: "Blue Sea 5025 six-circuit ST Blade block, rated 100 A total and 30 A per circuit. Only the house loads pass through its ATC slots. The 50 A charger connects before the house load switch and bypasses the branch fuse slots.",
    }),
    part("plotter", "load-chartplotter", 0, 280, {
      label: "Chartplotter", terminalOverrides: { pos: pin("right", .3), neg: pin("right", .75) },
      notes: "Generic example load; replace its draw and branch fuse with the actual equipment manual's values.",
    }),
    part("lights", "load-nav-lanterns-led", 0, 550, {
      label: "Navigation lights", terminalOverrides: { pos: pin("right", .3), neg: pin("right", .75) },
      notes: "Generic example load; replace its draw and branch fuse with the actual equipment manual's values.",
    }),
    part("negative", "dist-neg-bus", 700, 170, { rotation: 180, label: "Common negative", studCount: 5 }),
  ];
  const charging = { wireSizeMm2Override: 16 };
  const cables = [
    // Orion XS manual §3.3: 60–70 A fuses at both battery-positive sources,
    // minimum 16 mm² conductors for runs shorter than 5 m.
    wire("starter-feed", ["starter", "pos"], ["charger", "in-pos"], 2, charging),
    wire("starter-return", ["starter", "neg"], ["negative", "a"], 1.6, charging),
    wire("charge-feed", ["charger", "out-pos"], ["positive", "j"], .65, charging),
    wire("battery-feed", ["house", "pos"], ["positive", "j"], .15, charging),
    wire("charge-ground", ["charger", "out-neg"], ["negative", "e"], .4, charging),
    wire("battery-return", ["house", "neg"], ["negative", "d"], .4, charging),
    wire("house-feed", ["positive", "j"], ["main", "a"], .2, { fuseAmpOverride: 15, fuseTypeOverride: "ATC" }),
    wire("panel-feed", ["main", "b"], ["panel", "in-pos"], .3),
    wire("panel-return", ["panel", "in-neg"], ["negative", "b"], .5),
    wire("plotter-feed", ["panel", "c1"], ["plotter", "pos"], 2, { fuseAmpOverride: 7.5, fuseTypeOverride: "ATC" }),
    wire("plotter-return", ["plotter", "neg"], ["panel", "n1"], 2),
    wire("lights-feed", ["panel", "c4"], ["lights", "pos"], 3, { fuseAmpOverride: 3, fuseTypeOverride: "ATC" }),
    wire("lights-return", ["lights", "neg"], ["panel", "n4"], 3),
  ].map((cable, index) => ({ ...cable, label: `C${index + 1}` }));
  return routeExample({ name: "Boat — LiFePO4 house battery retrofit", nodes, cables, zoneDistances: {}, version: SCHEMA_VERSION }, {
    "starter-feed": viaX(300),
    "starter-return": (a, b) => [{ x: a.x, y: -30 }, { x: 800, y: -30 }, { x: 800, y: b.y }],
    "charge-feed": (a, b) => [{ x: a.x, y: 210 }, { x: b.x, y: 210 }],
    "battery-feed": viaX(350),
    "charge-ground": viaX(660),
    "battery-return": viaX(640),
    "house-feed": (a, b) => [{ x: b.x, y: a.y }],
    "panel-feed": (a, b) => [{ x: a.x, y: 455 }, { x: b.x, y: 455 }],
    "panel-return": viaX(680),
    "plotter-feed": viaX(240), "plotter-return": viaX(220),
    "lights-feed": viaX(240), "lights-return": viaX(220),
  });
}
